/**
 * Navigation guard hook for Budget Management forms
 * Reuses global guard state from settings pages
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { setNavigationGuardState } from '../pages/settings/useNavigationGuardState';

interface UseBudgetNavigationGuardResult {
  showNavigationDialog: boolean;
  pendingAction: (() => void) | null;
  requestNavigation: (action: () => void) => void;
  confirmDiscard: () => void;
  cancelNavigation: () => void;
  setHasUnsavedChanges: (dirty: boolean) => void;
  hasUnsavedChanges: boolean;
}

export function useBudgetNavigationGuard(): UseBudgetNavigationGuardResult {
  const navigate = useNavigate();
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showNavigationDialog, setShowNavigationDialog] = useState(false);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);

  const handleNavigationAttempt = useCallback(
    (path: string) => {
      if (hasUnsavedChanges) {
        setPendingAction(() => () => navigate(path));
        setShowNavigationDialog(true);
      } else {
        navigate(path);
      }
    },
    [hasUnsavedChanges, navigate]
  );

  useEffect(() => {
    setNavigationGuardState(hasUnsavedChanges, handleNavigationAttempt);
    return () => setNavigationGuardState(false, null);
  }, [hasUnsavedChanges, handleNavigationAttempt]);

  const requestNavigation = useCallback(
    (action: () => void) => {
      if (hasUnsavedChanges) {
        setPendingAction(() => action);
        setShowNavigationDialog(true);
      } else {
        action();
      }
    },
    [hasUnsavedChanges]
  );

  const confirmDiscard = useCallback(() => {
    setHasUnsavedChanges(false);
    setShowNavigationDialog(false);
    const action = pendingAction;
    setPendingAction(null);
    action?.();
  }, [pendingAction]);

  const cancelNavigation = useCallback(() => {
    setShowNavigationDialog(false);
    setPendingAction(null);
  }, []);

  return {
    showNavigationDialog,
    pendingAction,
    requestNavigation,
    confirmDiscard,
    cancelNavigation,
    setHasUnsavedChanges,
    hasUnsavedChanges,
  };
}

export { setNavigationGuardState };
